import React, { useEffect, useMemo, useState } from "react";
import { Pohrebka2Provider, usePohrebka2 } from "../context/Pohrebka2Context";
import { CLEAN_TABS } from "../utils/orderDefaults";
import Pohrebka2ZakladForm from "../components/zakazka/Pohrebka2ZakladForm";
import Pohrebka2VstupniForm from "../components/zakazka/Pohrebka2VstupniForm";
import Pohrebka2VypravitelForm from "../components/zakazka/Pohrebka2VypravitelForm";
import Pohrebka2ZesnulaForm from "../components/zakazka/Pohrebka2ZesnulaForm";
import Pohrebka2VyjezdForm from "../components/zakazka/Pohrebka2VyjezdForm";
import Pohrebka2FinalForm from "../components/zakazka/Pohrebka2FinalForm";
import Pohrebka2RecForm from "../components/zakazka/Pohrebka2RecForm";
import Pohrebka2PrevozForm from "../components/zakazka/Pohrebka2PrevozForm";
import Pohrebka2ObradForm from "../components/zakazka/Pohrebka2ObradForm";
import Pohrebka2VyberSluzebForm from "../components/zakazka/Pohrebka2VyberSluzebForm";
import Pohrebka2KvetinyForm from "../components/zakazka/Pohrebka2KvetinyForm";
import Pohrebka2PlatbaForm from "../components/zakazka/Pohrebka2PlatbaForm";
import Pohrebka2FotkyForm from "../components/zakazka/Pohrebka2FotkyForm";
import Pohrebka2CenikForm from "../components/zakazka/Pohrebka2CenikForm";
import Pohrebka2CalendarPanel from "../components/Pohrebka2CalendarPanel";
import Pohrebka2BookPanel from "../components/Pohrebka2BookPanel";
import {
  STAFF_OPTIONS,
  VEHICLE_OPTIONS,
  getSpeakerLabel,
  getMusicPersonLabel,
} from "../utils/calendarRoles";

const PLACE_ADDRESS_MAP = {
  "Obřadní síň Český Krumlov": "Hřbitovní 149, 381 01 Český Krumlov",
  "Kostel Český Krumlov": "Kostelní, 381 01 Český Krumlov",
  "Kostel Kaplice": "Kostelní, 382 41 Kaplice",
  "Kostel Vyšší Brod": "Klášterní, 382 73 Vyšší Brod",
  "Kostel Frymburk": "Náměstí, 382 79 Frymburk",
  "Kostel Horní Planá": "Náměstí, 382 26 Horní Planá",
  "Hřbitov Český Krumlov": "Chvalšinská, 381 01 Český Krumlov",
};

const shellStyle = {
  minHeight: "100vh",
  background: "#f5f7fb",
  color: "#111827",
};

const wrapStyle = {
  maxWidth: 1400,
  margin: "0 auto",
  padding: 24,
};

const topCardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  padding: 20,
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
};

const navStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 16,
};

const tabBtn = (active) => ({
  border: active ? "1px solid #111827" : "1px solid #d1d5db",
  background: active ? "#111827" : "#fff",
  color: active ? "#fff" : "#111827",
  borderRadius: 12,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
});

const cardStyle = {
  marginTop: 18,
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  padding: 20,
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  outline: "none",
};

function computeUiOrderStatus(order) {
  const src = order || {};
  const zaklad = src?.zaklad || {};
  const obrad = src?.obrad || {};
  const prevoz = src?.prevoz || {};
  const platba = src?.platba || {};

  const rawState = String(
    zaklad?.stavZakazky ||
    src?.stavKalendar ||
    src?.stavZakazky ||
    ""
  ).trim().toLowerCase();

  const obradDatum = String(obrad?.datum || "").trim();
  const obradCas = String(obrad?.cas || "").trim();
  const prevozDatum = String(prevoz?.datumPrevozu || "").trim();
  const prevozCas = String(prevoz?.casPrevozu || "").trim();

  let eventDateTime = null;

  if (obradDatum) {
    const dt = `${obradDatum}T${(obradCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  } else if (prevozDatum) {
    const dt = `${prevozDatum}T${(prevozCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  }

  const now = new Date();
  const eventPassed = !!eventDateTime && eventDateTime.getTime() <= now.getTime();

  const uhrazeno = Number(platba?.uhrazenoCelkem || 0) || 0;
  const zaloha = Number(platba?.zalohovaPlatba || 0) || 0;

  if (rawState.includes("storno")) {
    return { id: "storno", label: "Storno", tone: "red" };
  }

  if (rawState.includes("hotovo") || rawState.includes("uzavreno") || rawState.includes("uzavřeno")) {
    return { id: "hotovo", label: "Hotovo", tone: "green" };
  }

  if (eventPassed) {
    if (uhrazeno > 0) {
      return { id: "hotovo", label: "Hotovo", tone: "green" };
    }
    return { id: "cek_fakturace", label: "Hotovo / čeká na fakturaci", tone: "amber" };
  }

  if (zaloha > 0 && uhrazeno <= 0) {
    return { id: "doplatek", label: "Čeká na doplatek", tone: "amber" };
  }

  if (eventDateTime) {
    return { id: "cek_obrad", label: "Čeká na obřad", tone: "blue" };
  }

  return { id: "rozpracovano", label: "Rozpracováno", tone: "slate" };
}

function uiOrderStatusStyle(status) {
  const tone = String(status?.tone || "slate");

  if (tone === "green") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }

  if (tone === "blue") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }

  if (tone === "amber") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: "#fffbeb",
      color: "#92400e",
      border: "1px solid #fde68a",
    };
  }

  if (tone === "red") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: "#fef2f2",
      color: "#991b1b",
      border: "1px solid #fecaca",
    };
  }

  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: "#f8fafc",
    color: "#475569",
    border: "1px solid #cbd5e1",
  };
}

function Pohrebka2Inner() {
  const {
    activeTab,
    setActiveTab,
    currentOrderId,
    error,
    startNewOrder,
    loadOrders,
    ordersList,
    allTemplates,
    openOrder,
    loading,
    applyTemplateToCurrentOrder,
    saveCurrentOrderAsTemplate,
    order,
    setOrder,
      saveOrder,
  } = usePohrebka2();

  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showCenikEditor, setShowCenikEditor] = useState(false);
  const [showVyjezd, setShowVyjezd] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState("");

  const orderOptions = useMemo(() => {
      return (Array.isArray(ordersList) ? ordersList : []).map((item) => {
        const src =
          item?.formData ||
          item?.data ||
          item?.order ||
          item?.item ||
          item ||
          {};

        const zaklad = src?.zaklad || {};
        const vstupni = src?.vstupniInformace || {};
        const zesnula = src?.zesnulaOsoba || {};

        const id = String(
          item?.id ||
          item?.orderId ||
          item?.zakazkaId ||
          item?.cisloZakazky ||
          src?.id ||
          src?.orderId ||
          src?.zakazkaId ||
          zaklad?.cisloZakazky ||
          ""
        ).trim();

        const zesnuly = String(
          zaklad?.zesnuly ||
          [vstupni?.jmenoZesnuleho, vstupni?.prijmeniZesnuleho].filter(Boolean).join(" ") ||
          [zesnula?.jmeno, zesnula?.prijmeni].filter(Boolean).join(" ") ||
          item?.zesnuly ||
          item?.jmenoZesnuleho ||
          item?.jmeno ||
          item?.nazev ||
          ""
        ).trim();

        const datum = String(
          (src?.obrad || {})?.datum ||
          (src?.prevoz || {})?.datumPrevozu ||
          zaklad?.datumSjednani ||
          item?.datumSjednani ||
          item?.datumVytvoreni ||
          item?.datum ||
          src?.datumSjednani ||
          src?.datumVytvoreni ||
          ""
        ).trim();

        const status = computeUiOrderStatus(src);
        const statusLabel = String(status?.label || "").trim();

        
          const photoCount = Number(item?.photoCount || src?.photoCount || 0) || 0;
          const hasOhledaciList = !!(item?.hasOhledaciList || src?.hasOhledaciList);

          const extraParts = [];
          if (photoCount > 0) extraParts.push(`📷 ${photoCount}`);
          if (hasOhledaciList) extraParts.push("📄 ohledací list");

const labelParts = [statusLabel, id, zesnuly, datum].filter(Boolean);

        return {
          id,
          label: labelParts.length ? labelParts.join(" • ") : id || "Bez názvu",
        };
      }).filter((x) => x.id);
    }, [ordersList]);

  const calendarDraftSummary = useMemo(() => {
    const zaklad = order?.zaklad || {};
    const obrad = order?.obrad || {};
    const prevoz = order?.prevoz || {};
    const vstupni = order?.vstupniInformace || {};
    const zesnula = order?.zesnulaOsoba || {};

    const typ = String(zaklad?.typZakazky || "").trim();
    const datum =
      String(obrad?.datum || "").trim() ||
      String(prevoz?.datumPrevozu || "").trim();

    const cas =
      String(obrad?.cas || "").trim() ||
      String(prevoz?.casPrevozu || "").trim();

    const misto =
      String(obrad?.misto || "").trim() ||
      String(prevoz?.mistoOdvozu || "").trim() ||
      String(prevoz?.adresaOdvozu || "").trim();

    const zesnuly = String(
      [
        vstupni?.jmenoZesnuleho || zesnula?.jmeno || "",
        vstupni?.prijmeniZesnuleho || zesnula?.prijmeni || "",
      ].filter(Boolean).join(" ")
    ).trim();

    const objednavatel = String(
      [
        order?.vypravitel?.jmeno || "",
        order?.vypravitel?.prijmeni || "",
      ].filter(Boolean).join(" ")
    ).trim();

    const poznamka =
      String(obrad?.poznamka || "").trim() ||
      String(prevoz?.poznamka || "").trim() ||
      String(zaklad?.poznamka || "").trim();

    let typLabel = "";
    if (typ === "kremace") typLabel = "Kremace";
    else if (typ === "kremace_bez_obradu") typLabel = "Kremace bez obřadu";
    else if (typ === "pohreb") typLabel = "Pohřeb";
    else if (typ === "obrad_s_urnou") typLabel = "Obřad s urnou";
    else if (typ === "alternativa") typLabel = "Alternativa";
    else if (typ === "prevoz") typLabel = "Převoz";
    else if (typ === "ostatni") typLabel = "Ostatní";

    if (!typLabel && !datum && !cas && !misto && !zesnuly && !objednavatel && !poznamka) return null;

    return {
      typ,
      typLabel: typLabel || "Neurčeno",
      datum,
      cas,
      misto,
      zesnuly,
      objednavatel,
      poznamka,
    };
  }, [order]);

  const currentOrderStatus = useMemo(() => computeUiOrderStatus(order), [order]);

  const openOrderAndSyncUi = async (id, preferredTab = "zaklad", options = {}) => {
    const zid = String(id || "").trim();
    if (!zid) return;

    const nextTab = String(preferredTab || "zaklad").trim() || "zaklad";
    const keepBookOpen = !!options.keepBookOpen;
    const clearCalendarDate = options.clearCalendarDate !== false;

    await openOrder(zid);

    setSelectedOrderId(zid);
    if (clearCalendarDate) setCalendarSelectedDate("");
    if (!keepBookOpen) setShowBook(false);
    setActiveTab(nextTab);

    setTimeout(() => {
      try {
        document.getElementById("pohrebka2-form-start")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } catch {}
    }, 80);
  };


  useEffect(() => {
    const raw = String(window.location.hash || "");
    if (!raw.startsWith("#template:")) return;

    const templateId = raw.replace("#template:", "").trim();
    if (!templateId) return;

    if (typeof applyTemplateToCurrentOrder === "function") {
      applyTemplateToCurrentOrder(templateId);
      window.location.hash = "";
      setActiveTab("zaklad");
    }
  }, [applyTemplateToCurrentOrder, setActiveTab]);

  useEffect(() => {
    loadOrders().catch(() => {});
  }, [loadOrders]);

  useEffect(() => {
    if (!selectedOrderId && currentOrderId) {
      setSelectedOrderId(String(currentOrderId || ""));
    }
  }, [currentOrderId, selectedOrderId]);
  return (
    <div style={shellStyle}>
      <div style={wrapStyle}>
        <div style={topCardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => (window.location.href = "/")}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                background: "#ffffff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              ← Hlavní menu
            </button>

            <button
              type="button"
              onClick={() => {
                  setShowCenikEditor((prev) => {
                    const next = !prev;
                    if (next) setShowBook(false);
                    return next;
                  });
                }}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                background: showCenikEditor ? "#eff6ff" : "#ffffff",
                color: showCenikEditor ? "#1d4ed8" : "#111827",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              {showCenikEditor ? "Zavřít ceník" : "Ceník"}
            </button>

              <button
                type="button"
                onClick={() => {
                  setShowBook((prev) => {
                    const next = !prev;
                    if (next) setShowCenikEditor(false);
                    return next;
                  });
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: showBook ? "#eff6ff" : "#ffffff",
                  color: showBook ? "#1d4ed8" : "#111827",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {showBook ? "Zavřít knihu" : "Kniha zesnulých"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowVyjezd((prev) => !prev);
                  setShowBook(false);
                  setShowCenikEditor(false);
                }}
                style={{
                  ...tabBtn(false),
                  background: showVyjezd ? "#eff6ff" : "#ffffff",
                  color: showVyjezd ? "#1d4ed8" : "#111827",
                  border: showVyjezd ? "1px solid #bfdbfe" : "1px solid #d1d5db",
                }}
              >
                {showVyjezd ? "Zavřít výjezd" : "Výjezd"}
              </button>
          </div>

          <Pohrebka2CalendarPanel
            ordersList={ordersList}
            allTemplates={allTemplates}
            selectedDate={calendarSelectedDate}
            onSelectDate={(date) => setCalendarSelectedDate(String(date || ""))}
            onOpenOrder={async (id, preferredTab = "", row = null) => {
                if (!id) return;

                const nextTab =
                  preferredTab === "prevoz"
                    ? "prevoz"
                    : preferredTab === "zaklad"
                      ? "zaklad"
                      : preferredTab === "obrad"
                        ? "obrad"
                        : "obrad";

                await openOrderAndSyncUi(id, nextTab, { keepBookOpen: false, clearCalendarDate: true });
              }}
            onCreateFromDate={(date, time = "", typZakazky = "", misto = "", objednavatel = "", zesnuly = "", poznamka = "", assignments = {}) => {
              const d = String(date || "").slice(0, 10);
              const t = String(time || "").slice(0, 5);
              const typ = String(typZakazky || "").trim();
              const place = String(misto || "").trim();
              const placeId = String(assignments?.mistoId || "").trim();
              const placeGroup = String(assignments?.mistoSkupina || assignments?.placeType || "").trim();
              const placeLabel = String(assignments?.placeLabel || place || "").trim();
              const ceremonyVariant = String(assignments?.ceremonyVariant || "").trim();
              const churchServiceType = String(assignments?.churchServiceType || "").trim();
              const placeAddress =
                String(
                  PLACE_ADDRESS_MAP[placeId] ||
                  PLACE_ADDRESS_MAP[placeLabel] ||
                  PLACE_ADDRESS_MAP[place] ||
                  ""
                ).trim();
              const client = String(objednavatel || "").trim();
              const deceased = String(zesnuly || "").trim();
              const farar = String(assignments?.farar || "").trim();
              const note = String(poznamka || "").trim();

              const leader = String(assignments?.leader || "").trim();
              const support = String(assignments?.support || "").trim();
              const speaker = String(assignments?.speaker || "").trim();
              const speakerCustom = String(assignments?.speakerCustom || "").trim();
              const musicType = String(assignments?.musicType || "").trim();
              const musicPerson = String(assignments?.musicPerson || "").trim();
              const musicPersonCustom = String(assignments?.musicPersonCustom || "").trim();
              const vehicle = String(assignments?.vehicle || "").trim();
              const templateId = String(assignments?.templateId || "").trim();
              const coolingScenario = String(assignments?.coolingScenario || "").trim();
              const deathScenario = String(assignments?.deathScenario || "").trim();
              const prubehKey = String(assignments?.prubehKey || "").trim();
              const krematoriumAdresa = String(assignments?.krematoriumAdresa || "").trim();
              const krematoriumNazev = String(assignments?.krematoriumNazev || "").trim();
              const manualVystaveniMisto = String(assignments?.manualVystaveniMisto || "").trim();

              const staffLabelById = (id) =>
                (STAFF_OPTIONS || []).find((item) => String(item.id || "").trim() === String(id || "").trim())?.label || id;

              const vehicleLabelById = (id) =>
                (VEHICLE_OPTIONS || []).find((item) => String(item.id || "").trim() === String(id || "").trim())?.label || id;

              const resolvedSpeaker =
                speaker === "dalsi" ? speakerCustom : speaker;

              const resolvedMusicPerson =
                musicPerson === "dalsi" ? musicPersonCustom : musicPerson;

              const resolvedSpeakerLabel =
                speaker === "dalsi"
                  ? speakerCustom
                  : getSpeakerLabel(resolvedSpeaker);

              const resolvedMusicPersonLabel =
                musicPerson === "dalsi"
                  ? musicPersonCustom
                  : getMusicPersonLabel(resolvedMusicPerson);

              const obradRoleAssignments = [
                leader
                  ? {
                      role: "vedouci_obradu",
                      personId: leader,
                      personLabel: staffLabelById(leader),
                    }
                  : null,
                support && support !== leader
                  ? {
                      role: "obradni_sluzba",
                      personId: support,
                      personLabel: staffLabelById(support),
                    }
                  : null,
                resolvedSpeaker
                  ? {
                      role: "recnik",
                      personId: resolvedSpeaker,
                      personLabel: resolvedSpeakerLabel || resolvedSpeaker,
                    }
                  : null,
                resolvedMusicPerson
                  ? {
                      role: "hudba",
                      personId: resolvedMusicPerson,
                      personLabel: resolvedMusicPersonLabel || resolvedMusicPerson,
                    }
                  : null,
              ].filter(Boolean);

              const obradVehicleAssignments =
                vehicle && typ !== "prevoz"
                  ? [
                      {
                        role: "hlavni_vuz",
                        vehicleId: vehicle,
                        vehicleLabel: vehicleLabelById(vehicle),
                      },
                    ]
                  : [];

              const nextTab = typ === "prevoz" ? "prevoz" : "obrad";

              const clientParts = client.split(/\s+/).filter(Boolean);
              const clientJmeno = clientParts.shift() || "";
              const clientPrijmeni = clientParts.join(" ");

              const deceasedParts = deceased.split(/\s+/).filter(Boolean);
              const deceasedJmeno = deceasedParts.shift() || "";
              const deceasedPrijmeni = deceasedParts.join(" ");

              let obradTyp = "";
              if (typ === "kremace") obradTyp = "kremace";
              else if (typ === "kremace_bez_obradu") obradTyp = "kremace_bez_obradu";
              else if (typ === "pohreb") obradTyp = "pohreb";
              else if (typ === "alternativa") obradTyp = "alternativa";
              else if (typ === "obrad_s_urnou") obradTyp = "obrad_s_urnou";
              else if (typ === "prevoz") obradTyp = "samostatny_prevoz";
              else if (typ === "ostatni") obradTyp = "ostatni";

              startNewOrder();
              setSelectedOrderId("");
              setCalendarSelectedDate(d);

              if (templateId && typeof applyTemplateToCurrentOrder === "function") {
                try {
                  applyTemplateToCurrentOrder(templateId);
                } catch {}
              }

              setTimeout(() => {
                try {
                  setOrder((prev) => {
                    const base = prev || {};
                    return {
                      ...base,
                      zaklad: {
                        ...(base.zaklad || {}),
                        ...(typ ? { typZakazky: typ } : {}),
                        ...(deathScenario ? { scenarUmrti: deathScenario } : {}),
                        ...(prubehKey ? { prubehKey } : {}),
                        ...(note ? { poznamka: note } : {}),
                        ...(templateId ? { selectedTemplateId: templateId } : {}),
                        ...(templateId ? { selectedTemplateName: (allTemplates || []).find((x) => String(x?.id || "").trim() === templateId)?.name || "" } : {}),

                      },
                      vstupniInformace: {
                        ...(base.vstupniInformace || {}),
                        ...(deceasedJmeno ? { jmenoZesnuleho: deceasedJmeno } : {}),
                        ...(deceasedPrijmeni ? { prijmeniZesnuleho: deceasedPrijmeni } : {}),
                      },
                      zesnulaOsoba: {
                        ...(base.zesnulaOsoba || {}),
                        ...(deceasedJmeno ? { jmeno: deceasedJmeno } : {}),
                        ...(deceasedPrijmeni ? { prijmeni: deceasedPrijmeni } : {}),
                      },
                      vypravitel: {
                        ...(base.vypravitel || {}),
                        ...(clientJmeno ? { jmeno: clientJmeno } : {}),
                        ...(clientPrijmeni ? { prijmeni: clientPrijmeni } : {}),
                      },
                      obrad: {
                        ...(base.obrad || {}),
                        ...(d ? { datum: d } : {}),
                        ...(t ? { cas: t } : {}),
                        ...(obradTyp ? { typ: obradTyp } : {}),
                        ...((typ !== "prevoz" && placeLabel && placeLabel !== "__manual__") ? { misto: placeLabel } : {}),
                        ...((typ !== "prevoz" && placeId) ? { mistoId: placeId } : {}),
                        ...((typ !== "prevoz" && placeGroup) ? { mistoSkupina: placeGroup } : {}),
                        ...((typ !== "prevoz" && ceremonyVariant) ? { ceremonyVariant } : {}),
                        ...((typ !== "prevoz" && churchServiceType) ? { churchServiceType } : {}),
                        ...((typ !== "prevoz" && placeAddress) ? { mistoAdresa: placeAddress } : {}),
                        ...((typ === "kremace_bez_obradu" && krematoriumNazev) ? { krematorium: krematoriumNazev } : {}),
                        ...((typ === "kremace_bez_obradu" && krematoriumAdresa) ? { krematoriumAdresa } : {}),
                        ...((typ === "kremace_bez_obradu" && manualVystaveniMisto) ? { misto: manualVystaveniMisto } : {}),
                        ...(note ? { poznamka: note } : {}),
                        ...(typ !== "prevoz" && farar ? { fararJmeno: farar } : {}),
                        ...(typ !== "prevoz" ? { roleAssignments: obradRoleAssignments } : {}),
                        ...(typ !== "prevoz" ? { vehicleAssignments: obradVehicleAssignments } : {}),
                        ...(typ !== "prevoz" && speaker ? { recnikTyp: speaker } : {}),
                        ...(typ !== "prevoz" && speaker === "dalsi" && speakerCustom ? { recnikJmeno: speakerCustom } : {}),
                        ...(typ !== "prevoz" && musicType ? { hudbaTyp: musicType } : {}),
                        ...(typ !== "prevoz" && resolvedMusicPerson ? { hudbaZajistuje: resolvedMusicPerson } : {}),
                      },
                      prevoz: {
                        ...(base.prevoz || {}),
                        ...(coolingScenario ? { coolingScenario } : {}),
                        ...(typ === "prevoz" && d ? { datumPrevozu: d } : {}),
                        ...(typ === "prevoz" && t ? { casPrevozu: t } : {}),
                        ...(typ === "prevoz" && placeLabel && placeLabel !== "__manual__" ? { mistoOdvozu: placeLabel } : {}),
                        ...(typ === "prevoz" && placeAddress ? { adresaOdvozu: placeAddress } : {}),
                        ...(typ === "prevoz" && note ? { poznamka: note } : {}),
                        ...(typ === "prevoz"
                          ? {
                              roleAssignments: [
                                leader
                                  ? {
                                      role: "ridic_prevozu",
                                      personId: leader,
                                      personLabel: staffLabelById(leader),
                                    }
                                  : null,
                                support && support !== leader
                                  ? {
                                      role: "doprovod_prevozu",
                                      personId: support,
                                      personLabel: staffLabelById(support),
                                    }
                                  : null,
                              ].filter(Boolean),
                            }
                          : {}),
                        ...(typ === "prevoz" && vehicle
                          ? {
                              vehicleAssignments: [
                                {
                                  role: "prevozove_auto",
                                  vehicleId: vehicle,
                                  vehicleLabel: vehicleLabelById(vehicle),
                                },
                              ],
                            }
                          : {}),
                      },
                    };
                  });

                  const ev = new CustomEvent("pohrebka2-calendar-create-date", {
                    detail: {
                      date: d,
                      time: t,
                      typZakazky: typ,
                      misto: placeLabel || place,
                      mistoId: placeId,
                      mistoSkupina: placeGroup,
                      ceremonyVariant,
                      churchServiceType,
                      mistoAdresa: placeAddress,
                      objednavatel: client,
                      zesnuly: deceased,
                      poznamka: note,
                      assignments: {
                        ...(assignments || {}),
                        templateId,
                        coolingScenario,
                        deathScenario,
                        prubehKey,
                      },
                    },
                  });
                  window.dispatchEvent(ev);
                } catch {}
              }, 0);

              setActiveTab(nextTab);
              setTimeout(() => {
                try {
                  document.getElementById("pohrebka2-form-start")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } catch {}
              }, 120);
            }}
          />


<div id="pohrebka2-form-start" style={{ position: "relative", top: "-8px" }} />
<div style={{ fontSize: 30, fontWeight: 900 }}>Pohřebka 2</div>
              <div style={{ opacity: 0.75 }}>Čistá nová větev oddělená od staré aplikace</div>
            </div>

            <div style={{ textAlign: "right", minWidth: 280 }}>
              <div style={{ fontWeight: 800 }}>Aktivní zakázka</div>
              <div>{currentOrderId || "Nová zakázka"}</div>

                <div style={{ marginTop: 8 }}>
                  <span style={uiOrderStatusStyle(currentOrderStatus)}>
                    {currentOrderStatus?.label || "Rozpracováno"}
                  </span>
                </div>
              {order?.meta?.selectedTemplateName ? (
                <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                  Vzor: <strong>{order.meta.selectedTemplateName}</strong>
                </div>
              ) : null}
            </div>
          </div>

            <div style={navStyle}>
              <button
                type="button"
                onClick={() => {
                  startNewOrder();
                  setSelectedOrderId("");
                  setCalendarSelectedDate("");
                  setActiveTab("zaklad");
                }}
                style={tabBtn(false)}
              >
                + Nová zakázka
              </button>

              <button
                type="button"
                onClick={() => {
                  const name = window.prompt("Název nového vzoru:");
                  if (!name) return;
                  const ok = saveCurrentOrderAsTemplate(name);
                  if (ok) window.alert("Vzor byl uložen.");
                }}
                style={tabBtn(false)}
              >
                Uložit jako vzor
              </button>

              <button
                type="button"
                onClick={() => loadOrders().catch(() => {})}
                style={tabBtn(false)}
              >
                {loading ? "Načítám..." : "Načíst zakázky"}
              </button>

              <select
                  value={selectedTemplateId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedTemplateId(id);
                    if (!id) return;

                    const ok = window.confirm("Načíst vybraný vzor do aktivní zakázky?");
                    if (!ok) {
                      setSelectedTemplateId("");
                      return;
                    }

                    if (typeof applyTemplateToCurrentOrder === "function") {
                      applyTemplateToCurrentOrder(id);
                      setActiveTab("zaklad");
                    }

                    setSelectedTemplateId("");
                  }}
                  style={{
                  minWidth: 320,
                  maxWidth: 460,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontWeight: 600,
                }}
              >
                <option value="">Načíst vzor zakázky</option>
                {(allTemplates || []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.isSystem ? `Systém • ${item.name}` : `Vlastní • ${item.name}`}
                  </option>
                ))}
              </select>

              <select
                value={selectedOrderId}
                onChange={(e) => setSelectedOrderId(e.target.value)}
                style={{
                  minWidth: 320,
                  maxWidth: 460,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  borderRadius: 12,
                  padding: "10px 12px",
                  fontWeight: 600,
                }}
              >
                <option value="">Vyber existující zakázku</option>
                {orderOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={async () => {
                  if (!selectedOrderId) return;
                  await openOrder(selectedOrderId);
                  setCalendarSelectedDate("");
                  setShowVyjezd(false);
                  setActiveTab("zaklad");
                }}
                style={tabBtn(false)}
              >
                Otevřít zakázku
              </button>

              {CLEAN_TABS.filter((tab) => tab.id !== "vyjezd").map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setShowCenikEditor(false);
                    setShowBook(false);
                    setShowVyjezd(false);
                    setActiveTab(tab.id);
                  }}
                  style={tabBtn(activeTab === tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

          {error ? (
            <div style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 12,
              background: "#fff1f2",
              color: "#9f1239",
              border: "1px solid #fecdd3",
              fontWeight: 700,
            }}>
              {error}
            </div>
          ) : null}
        </div>





          {calendarDraftSummary ? (
            <div
              style={{
                marginTop: 14,
                marginBottom: 14,
                padding: 14,
                borderRadius: 16,
                border: "1px solid #dbeafe",
                background: "#f8fbff",
                boxShadow: "0 6px 18px rgba(37,99,235,0.06)",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8", marginBottom: 8 }}>
                Právě zakládáš z kalendáře
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  background: "#ffffff",
                  border: "1px solid #dbeafe",
                  fontWeight: 800,
                  color: "#111827",
                }}>
                  Typ: {calendarDraftSummary.typLabel}
                </div>

                <div style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  background: "#ffffff",
                  border: "1px solid #dbeafe",
                  fontWeight: 800,
                  color: "#111827",
                }}>
                  Datum: {calendarDraftSummary.datum || "-"}
                </div>

                <div style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  background: "#ffffff",
                  border: "1px solid #dbeafe",
                  fontWeight: 800,
                  color: "#111827",
                }}>
                  Čas: {calendarDraftSummary.cas || "-"}
                </div>

                  <div style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid #dbeafe",
                    fontWeight: 800,
                    color: "#111827",
                  }}>
                    Místo: {calendarDraftSummary.misto || "-"}
                  </div>

                  <div style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid #dbeafe",
                    fontWeight: 800,
                    color: "#111827",
                  }}>
                    Zesnulý: {calendarDraftSummary.zesnuly || "-"}
                  </div>

                  <div style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid #dbeafe",
                    fontWeight: 800,
                    color: "#111827",
                  }}>
                    Objednavatel: {calendarDraftSummary.objednavatel || "-"}
                  </div>

                  <div style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid #dbeafe",
                    fontWeight: 700,
                    color: "#374151",
                  }}>
                    Poznámka: {calendarDraftSummary.poznamka || "-"}
                  </div>
              </div>
            </div>
          ) : null}

          {showCenikEditor ? (
              <Pohrebka2CenikForm />
            ) : showBook ? (
              <Pohrebka2BookPanel
                ordersList={ordersList}
                order={order}
                setOrder={setOrder}
                saveOrder={saveOrder}
                onOpenOrder={async (id, preferredTab = "zaklad") => {
                  if (!id) return;
                  await openOrder(id);
                  setSelectedOrderId(String(id || ""));
                  setCalendarSelectedDate("");
                  setShowBook(false);
                  setShowVyjezd(false);
                  setActiveTab(preferredTab || "zaklad");

                  setTimeout(() => {
                    try {
                      document.getElementById("pohrebka2-form-start")?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    } catch {}
                  }, 80);
                }}
              />
            ) : showVyjezd ? (
              <Pohrebka2VyjezdForm />
            ) : (
              <>
          {activeTab === "zaklad" && <Pohrebka2ZakladForm />}
          {activeTab === "vstupni" && <Pohrebka2VstupniForm />}
          {activeTab === "vypravitel" && <Pohrebka2VypravitelForm />}
          {activeTab === "zesnula" && <Pohrebka2ZesnulaForm />}
          {activeTab === "obrad" && <Pohrebka2ObradForm />}
          {activeTab === "prevoz" && <Pohrebka2PrevozForm />}
          {activeTab === "sluzby" && <Pohrebka2VyberSluzebForm />}
          {activeTab === "kvetiny" && <Pohrebka2KvetinyForm />}
          {activeTab === "platba" && <Pohrebka2PlatbaForm />}
            {activeTab === "fotky" && <Pohrebka2FotkyForm />}
            {activeTab === "rec" && <Pohrebka2RecForm />}
            {activeTab === "final" && <Pohrebka2FinalForm />}
              </>
            )}
      </div>
    </div>
  );
}

export default function Pohrebka2App() {
  return (
    <Pohrebka2Provider>
      <Pohrebka2Inner />
    </Pohrebka2Provider>
  );
}
